import { inject, injectable } from 'inversify'
import { computed, makeObservable } from 'mobx'
import { Types } from '@core/ioc/types'
import type { FormatGateway } from '@core/services/format.gateway'
import { I18nRepository } from '@core/infra/i18n/i18n.repository'
import { OfflineCacheRepository } from '@core/infra/offline/offline-cache.repository'
import { ConnectivityRepository } from './connectivity.repository'

/**
 * Read side of online/offline: what OfflineBannerComponent renders.
 * Code that *changes* the state injects ConnectivityRepository.
 */
@injectable()
export class ConnectivityPresenter {
  @inject(ConnectivityRepository)
  private connectivityRepository!: ConnectivityRepository

  @inject(OfflineCacheRepository)
  private offlineCache!: OfflineCacheRepository

  @inject(I18nRepository)
  private i18nRepository!: I18nRepository

  @inject(Types.IFormatGateway)
  private formatGateway!: FormatGateway

  constructor() {
    makeObservable(this, {
      viewModel: computed,
    })
  }

  // One ViewModel object, not loose getters: the component asks "should I
  // render, and with what", never "what is the state" — `showOfflineBanner`
  // rather than `isOnline` is what stops the negation living in the markup.
  //
  // The two notices are independent: `navigator.onLine` lies on a captive
  // portal, so data can be stale while the app believes it is online — the
  // stale line must render on its own flag, not gated on the offline one.
  get viewModel(): {
    showOfflineBanner: boolean
    offlineMessage: string
    showStaleNotice: boolean
    staleMessage: string
  } {
    const servedAt = this.offlineCache.servedCachedAtMs()
    const showStaleNotice = servedAt.length > 0
    // The oldest entry among those served — the most pessimistic claim is the honest one.
    const oldest = showStaleNotice ? Math.min(...servedAt) : 0

    return {
      showOfflineBanner: !this.connectivityRepository.isOnlinePm,
      offlineMessage: this.i18nRepository.t('connectivity.offline'),
      showStaleNotice,
      staleMessage: showStaleNotice
        ? this.i18nRepository.t('connectivity.stale', {
            time: this.formatGateway.formatDate(new Date(oldest), { hour: 'numeric', minute: '2-digit' }),
          })
        : '',
    }
  }
}
